import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, ArrowRight, ArrowLeft, AlertTriangle, CheckCircle, HelpCircle, Cpu, Save, Cloud, LayoutDashboard } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import API_URL from '../lib/apiUrl';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { useAutoSave } from '../hooks/useAutoSave';


// Custom styled select item for colored options
const ColoredSelectItem = ({ value, color, children }) => {
  const colorClasses = {
    green: 'text-green-600',
    yellow: 'text-yellow-600',
    red: 'text-red-600',
    default: 'text-black'
  };
  
  return (
    <SelectItem value={value} className={colorClasses[color] || colorClasses.default}>
      {children}
    </SelectItem>
  );
};

// Display component for selected value with color
const SelectedValueDisplay = ({ value, options }) => {
  const option = options.find(opt => opt.value === value);
  if (!option) return <span className="text-slate-500">Select an option</span>;
  
  const colorClasses = {
    green: 'text-green-600 font-medium',
    yellow: 'text-yellow-600 font-medium',
    red: 'text-red-600 font-medium',
    default: 'text-black'
  };
  
  return <span className={colorClasses[option.color] || colorClasses.default}>{option.label}</span>;
};

const TechnicalControls = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  const [formData, setFormData] = useState({
    mfa_implemented: '',
    antivirus_edr: '',
    firewall_maintained: '',
    siem_logs_monitored: '',
    backups_automated: '',
    encryption_enabled: '',
    secure_configurations: '',
    endpoint_hardening: '',
    patch_automation: '',
    vulnerability_scanning: ''
  });

  // Auto-save hook - saves every 60 seconds
  const { 
    isSaving, 
    lastSaved, 
    saveDraft, 
    deleteDraft,
    saveSuccess
  } = useAutoSave('technical_controls', orgId, formData, setFormData, token, 30000, !initialLoading, false);

  // Load existing data on mount
  useEffect(() => {
    const loadExistingData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [persistedResponse, draftResponse] = await Promise.all([
          axios.get(`${API_URL}/api/technical-controls/${orgId}`, { headers, params: { optional: true } }),
          axios.get(`${API_URL}/api/drafts/technical_controls`, { headers, params: { org_id: orgId } }).catch(() => ({ data: null }))
        ]);
        setFormData((current) => ({ ...current, ...(persistedResponse.data || {}), ...(draftResponse.data?.draft?.data || {}) }));
      } catch (error) {
        console.log('No existing technical controls data');
      } finally {
        setInitialLoading(false);
      }
    };
    loadExistingData();
  }, [orgId, token]);

  // Track unsaved changes
  const updateFormData = useCallback((newData) => {
    setFormData(newData);
    setHasUnsavedChanges(true);
  }, []);

  // Browser tab close warning - always show on assessment pages
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const message = 'Data you entered is not saved. Are you sure you want to close?';
      e.preventDefault();
      e.returnValue = message;
      return message;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Standard Yes/No/Unaware options
  const standardOptions = [
    { value: 'yes', label: 'Yes', color: 'green' },
    { value: 'no', label: 'No', color: 'red' },
    { value: 'unaware', label: 'Unaware', color: 'yellow' }
  ];

  // Question definitions
  const questions = [
    {
      id: 'mfa_implemented',
      number: 1,
      question: 'MFA (Multi Factor Authentication) is implemented?',
      options: standardOptions
    },
    {
      id: 'antivirus_edr',
      number: 2,
      question: 'Antivirus/EDR implemented and followed regularly?',
      options: standardOptions
    },
    {
      id: 'firewall_maintained',
      number: 3,
      question: 'Firewall is used, maintained and updated timely?',
      options: standardOptions
    },
    {
      id: 'siem_logs_monitored',
      number: 4,
      question: 'SIEM/logs (Security Incident and Event Management) is monitored?',
      options: standardOptions
    },
    {
      id: 'backups_automated',
      number: 5,
      question: 'Backups automated for ensuring no data loss?',
      options: standardOptions
    },
    {
      id: 'encryption_enabled',
      number: 6,
      question: 'Encryption enabled for data security?',
      options: standardOptions
    },
    {
      id: 'secure_configurations',
      number: 7,
      question: 'Secure configurations are being practiced in the system?',
      options: standardOptions
    },
    {
      id: 'endpoint_hardening',
      number: 8,
      question: 'Endpoint hardening is defined, followed and monitored regularly?',
      options: standardOptions
    },
    {
      id: 'patch_automation',
      number: 9,
      question: 'Patch automation is being followed in the organization?',
      options: standardOptions
    },
    {
      id: 'vulnerability_scanning',
      number: 10,
      question: 'Vulnerability scanning is defined, followed and maintained regularly?',
      options: standardOptions
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all questions are answered
    for (const question of questions) {
      if (!formData[question.id]) {
        toast.error(`Please answer question ${question.number}: "${question.question}"`);
        return;
      }
    }
    
    setLoading(true);

    try {
      // Save technical controls data
      await axios.post(
        `${API_URL}/api/technical-controls`,
        { 
          org_id: orgId,
          ...formData 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setHasUnsavedChanges(false);
      await deleteDraft(); // Clean up draft after successful save
      toast.success('Technical controls assessment saved!');
      
      // Navigate to Documentation Readiness page
      navigate(`/documentation-readiness/${orgId}`);
    } catch (error) {
      toast.error('Failed to save technical controls assessment');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/core-assessment/${orgId}`);
  };

  // Get icon based on selected value color
  const getStatusIcon = (questionId) => {
    const value = formData[questionId];
    if (!value) return null;
    
    const question = questions.find(q => q.id === questionId);
    const option = question?.options.find(opt => opt.value === value);
    
    if (!option) return null;
    
    switch (option.color) {
      case 'green':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'yellow':
        return <HelpCircle className="h-5 w-5 text-yellow-500" />;
      case 'red':
        return <AlertTriangle className="h-5 w-5 text-red-500" />;
      default:
        return null;
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-slate-900 mx-auto mb-4" />
          <p className="font-body text-lg text-slate-600">Loading assessment data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 py-12">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Back to Dashboard Button */}
          <div className="mb-6">
            <Button 
              variant="outline" 
              onClick={() => navigate('/dashboard')}
              className="gap-2"
            >
              <LayoutDashboard className="h-4 w-4" />
              Back to Dashboard
            </Button>
          </div>

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <Cpu className="h-8 w-8 text-slate-900" />
              <h1 className="font-heading font-bold text-4xl text-black">
                Technical Controls
              </h1>
            </div>
            <p className="font-body text-lg text-slate-700">
              Assess your organization&apos;s technical security controls and infrastructure
            </p>
          </div>

          {/* Progress Indicator */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-xs uppercase tracking-widest text-slate-600">Step 3 of 4</span>
              <span className="font-mono text-xs text-slate-600">Technical Assessment</span>
            </div>
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-slate-900 w-3/4 progress-bar"></div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-8 space-y-6">
            
            {/* Questions */}
            {questions.map((question) => (
              <div key={question.id} className="pb-4 border-b border-slate-100 last:border-b-0">
                <Label className="font-body text-sm font-medium text-black mb-2 block">
                  {question.number}. {question.question} <span className="text-red-500">*</span>
                </Label>
                <div className="flex items-center gap-3">
                  <Select
                    value={formData[question.id]}
                    onValueChange={(value) => updateFormData({ ...formData, [question.id]: value })}
                  >
                    <SelectTrigger data-testid={`${question.id}-select`} className="text-black flex-1">
                      {formData[question.id] ? (
                        <SelectedValueDisplay value={formData[question.id]} options={question.options} />
                      ) : (
                        <span className="text-slate-500">Select an option</span>
                      )}
                    </SelectTrigger>
                    <SelectContent>
                      {question.options.map((option) => (
                        <ColoredSelectItem key={option.value} value={option.value} color={option.color}>
                          {option.label}
                        </ColoredSelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="w-6">
                    {getStatusIcon(question.id)}
                  </div>
                </div>
              </div>
            ))}

            {/* Navigation Buttons */}
            <div className="pt-6 border-t border-slate-200">
              {/* Save Draft Button with Status */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-sm">
                  {isSaving ? (
                    <span className="flex items-center gap-1 text-slate-500">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Saving...
                    </span>
                  ) : saveSuccess ? (
                    <span className="flex items-center gap-1 text-green-600 font-medium">
                      <Cloud className="h-4 w-4" />
                      Draft saved successfully
                    </span>
                  ) : lastSaved ? (
                    <span className="text-slate-500">
                      <Cloud className="h-4 w-4 inline mr-1" />
                      Last saved: {lastSaved.toLocaleTimeString()}
                    </span>
                  ) : null}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={saveDraft}
                  disabled={isSaving}
                  className="gap-2"
                >
                  <Save className="h-4 w-4" />
                  Save Draft
                </Button>
              </div>
              
              <div className="flex gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBack}
                  className="flex-1 py-6 text-base font-semibold"
                  data-testid="back-button"
                >
                  <ArrowLeft className="mr-2 h-5 w-5" />
                  Back
                </Button>
                <Button
                  type="submit"
                  className="flex-1 py-6 text-base font-semibold"
                  disabled={loading}
                  data-testid="next-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      Next
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default TechnicalControls;
